import { useState, useEffect, useRef, type FC, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LuBell, LuCheck, LuInbox, LuUserPlus, LuMessageSquare, LuListTodo, LuHandshake } from 'react-icons/lu';
import { NotificacaoService, type Aviso, type TipoAviso } from '../services/NotificacaoService';
import { AvisosNoAparelhoFaixa } from './AvisosNoAparelho';
import './NotificationsBell.css';

/**
 * O sino da sidebar — só AVISO: lead novo, cliente respondeu, tarefa atribuída a você e
 * negócio fechado. docs/designsystem/propostas/notificacoes.html §6 (direção C, fase 3).
 *
 * Tarefa não mora mais aqui: ela tem a tela /tarefas, onde se encerra pelo desfecho. Era
 * a mistura das duas coisas que fazia "dar como lido" ser perigoso — lido não é feito.
 * Aqui todo item é aviso, então lido pode significar "some", e some de vez: a marca vai
 * para o servidor, não para este navegador.
 */

const ICONE: Record<TipoAviso, ReactNode> = {
    lead: <LuUserPlus />,
    msg: <LuMessageSquare />,
    tarefa: <LuListTodo />,
    fechado: <LuHandshake />,
};

/** "há 12 min" é o que importa num aviso; a data exata está no destino do clique. */
const desde = (iso: string) => {
    const minutos = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    if (minutos < 1) return 'agora';
    if (minutos < 60) return `há ${minutos} min`;
    const horas = Math.round(minutos / 60);
    if (horas < 24) return `há ${horas} h`;
    const dias = Math.round(horas / 24);
    return `há ${dias} ${dias === 1 ? 'dia' : 'dias'}`;
};

interface Props {
    /** Avisos não lidos, contados pela sidebar junto com as tarefas vencidas. */
    naoLidos: number;
    /** Pede à sidebar que recarregue os dois números depois de uma marcação. */
    onContagemMudou: () => void;
}

const NotificationsBell: FC<Props> = ({ naoLidos, onContagemMudou }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [aberto, setAberto] = useState(false);
    const [carregando, setCarregando] = useState(false);
    const [avisos, setAvisos] = useState<Aviso[]>([]);
    const [verLidos, setVerLidos] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fora = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setAberto(false);
        };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, []);

    useEffect(() => { setAberto(false); }, [location.pathname]);

    const carregar = async () => {
        setCarregando(true);
        try {
            setAvisos(await NotificacaoService.getAvisos());
        } catch {
            setAvisos([]);
        } finally {
            setCarregando(false);
        }
    };

    const alternar = async () => {
        const proximo = !aberto;
        setAberto(proximo);
        if (proximo) { setVerLidos(false); await carregar(); }
    };

    /** Marca sem fechar o sino: a linha fica esmaecida e sai na próxima abertura. */
    const marcar = async (itens: Aviso[]) => {
        if (itens.length === 0) return;
        const chaves = itens.map(a => ({ tipo: a.tipo, chave: a.chave }));
        setAvisos(prev => prev.map(a =>
            chaves.some(c => c.tipo === a.tipo && c.chave === a.chave) ? { ...a, lido: true } : a));
        await NotificacaoService.marcarLido(chaves);
        onContagemMudou();
    };

    /** Abrir é ler: quem clicou no aviso já viu o que ele dizia. */
    const abrir = async (a: Aviso) => {
        setAberto(false);
        if (!a.lido) marcar([a]);
        if (a.conversaId) navigate(`/pipeline/deal/${a.conversaId}`);
        else if (a.tarefaId) navigate('/tarefas');
    };

    const lista = avisos.filter(a => a.lido === verLidos);
    const contaNaoLidos = avisos.filter(a => !a.lido).length;

    return (
        <div className="nb-container" ref={containerRef}>
            <button type="button" className="sb-icon-btn nb-trigger" onClick={alternar}
                title="Notificações" aria-label="Notificações">
                <LuBell />
                {naoLidos > 0 && <span className="nb-dot">{naoLidos > 9 ? '9+' : naoLidos}</span>}
            </button>

            {aberto && (
                <div className="nb-popover">
                    <div className="nb-tabs">
                        <button type="button" className={`nb-tab ${verLidos ? '' : 'is-active'}`}
                            onClick={() => setVerLidos(false)}>
                            <LuInbox />
                            <span>Não lidos</span>
                            <span className="nb-tab-count">{carregando ? '' : contaNaoLidos}</span>
                        </button>
                        <button type="button" className={`nb-tab ${verLidos ? 'is-active' : ''}`}
                            onClick={() => setVerLidos(true)}>
                            <LuCheck />
                            <span>Lidos</span>
                        </button>
                    </div>

                    <AvisosNoAparelhoFaixa />

                    <div className="nb-popover-body">
                        {carregando ? (
                            <p className="nb-empty">Carregando…</p>
                        ) : lista.length === 0 ? (
                            <p className="nb-empty">
                                {verLidos ? 'Nada lido nos últimos 7 dias.' : 'Nenhum aviso novo.'}
                            </p>
                        ) : (
                            lista.map(a => (
                                <div key={`${a.tipo}:${a.chave}`} className={`nb-item ${a.lido ? 'is-lido' : ''}`}>
                                    <button type="button" className="nb-item-main" onClick={() => abrir(a)}>
                                        <span className="nb-item-icon">{ICONE[a.tipo]}</span>
                                        <span className="nb-item-text">
                                            <span className="nb-item-name">{a.titulo}</span>
                                            <span className="nb-item-details">{a.detalhe} · {desde(a.quando)}</span>
                                        </span>
                                    </button>
                                    {!a.lido && (
                                        <button type="button" className="nb-check" onClick={() => marcar([a])}
                                            title="Marcar como lido" aria-label="Marcar como lido">
                                            <LuCheck />
                                        </button>
                                    )}
                                </div>
                            ))
                        )}
                    </div>

                    <div className="nb-foot">
                        <button type="button" className="nb-foot-btn"
                            disabled={contaNaoLidos === 0}
                            onClick={() => marcar(avisos.filter(a => !a.lido))}>
                            <LuCheck /> Marcar todos como lidos
                        </button>
                        {/* A porta para onde as tarefas foram: elas saíram do sino nesta fase. */}
                        <button type="button" className="nb-foot-link" onClick={() => { setAberto(false); navigate('/tarefas'); }}>
                            Ver tarefas
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationsBell;
